// packages/analytics/src/client.ts
import posthog, { type CaptureResult } from 'posthog-js'
// Pre-bundle the recorder. `dist/posthog-recorder` registers BOTH
// __PosthogExtensions__.rrweb AND initSessionRecording; `dist/recorder` registers
// only the former, so posthog-js still fetches over the network — which is
// CSP-blocked once disable_external_dependency_loading is on (R13).
import 'posthog-js/dist/posthog-recorder'

import { REPLAY_POLICY, type EventName, type EventProperties } from './registry'
import { flattenPatterns, sanitizeProperties } from './sanitize'
import { buildEnvelope, type Envelope, type UserRole } from './envelope'

const PATTERNS = flattenPatterns(REPLAY_POLICY.intelhub)

let enabled = false
let envelope: Envelope | null = null
let analyticsSuppressed = true

/** @internal — test seam only. */
export function __resetForTests(): void {
  enabled = false
  envelope = null
  analyticsSuppressed = true
}

/**
 * The single chokepoint every event passes through, wired as `before_send`.
 *
 * R4 (envelope on every event) and R22 (URL sanitisation) are BOTH enforced
 * here rather than in `capture()`. That is deliberate: `capture()` only covers
 * the registry events we call ourselves, so anything reaching `posthog.capture`
 * directly — `$pageview`, `$identify`, any future posthog-js internal event —
 * silently escaped the envelope. That is exactly what happened to `$pageview`
 * (measured: 62 events, zero carrying `product`/`env`/`user_role`), which in
 * turn made the weekly-active-CISOs insight permanently empty.
 *
 * Exported for direct unit testing; `before_send` is otherwise unreachable.
 */
export function decorateEvent(
  event: CaptureResult | null,
  patterns: string[],
): CaptureResult | null {
  if (!event) return event
  if (analyticsSuppressed) return null
  const props = sanitizeProperties(event.properties ?? {}, patterns)
  // Envelope last: it is ours and must not be overridable by event properties.
  event.properties = envelope ? { ...props, ...envelope } : props
  return event
}

export function isEnabled(): boolean {
  return enabled
}

export function setAnalyticsSuppressed(suppressed: boolean): void {
  analyticsSuppressed = suppressed
}

export function isAnalyticsSuppressed(): boolean {
  return analyticsSuppressed
}

export function initAnalytics(config: { key?: string; role: UserRole }): void {
  // R7: absence of the project key is a hard no-op. posthog-js is never
  // initialised, so there is no queue that could flush later.
  if (!config.key) {
    enabled = false
    return
  }

  const env = import.meta.env.PROD ? 'prod' : 'dev'
  envelope = buildEnvelope('intelhub', env, config.role)

  posthog.init(config.key, {
    // KAN-32 R13 — every PostHog request goes through our own origin.
    //
    // RELATIVE, not absolute, and that is deliberate. A relative api_host
    // resolves against whatever origin the app is served from, so the same
    // build works in the Vite dev server (which proxies /api to :3002), in the
    // Express-serves-web-dist topology the evidence spec uses, and in
    // production — with no build-time URL to get wrong and no way for the
    // browser to leave its own origin.
    //
    // posthog-js's RequestRouter classifies any non-PostHog api_host as region
    // `custom`, which collapses ALL of its targets — api, assets and flags —
    // onto this one base. So this single option redirects `/e/`, `/s/`,
    // `/flags/?v=2` and `/array/<token>/config` at once, and the server-side
    // allowlist in apps/api/src/routes/posthog-proxy.routes.ts is where the
    // ingest/assets split is put back.
    api_host: '/api/ph',
    // Must stay absolute and must stay the real PostHog origin: ui_host is
    // never fetched, it only builds the "view in PostHog" links posthog-js
    // prints. Without it, uiHost is derived from api_host and those links
    // would point at intel.cyberfind.io/api/ph.
    ui_host: 'https://eu.posthog.com',
    // P1: no new client-side storage. NOT cookieless_mode — that forces the
    // $posthog_cookieless sentinel and makes identify() impossible.
    persistence: 'memory',
    // Pinned. An unpinned default sets capture_pageview: 'history_change',
    // which double-counts against R14; >= '2025-11-30' also sets
    // strictMinimumDuration, silently dropping short replays.
    defaults: '2026-01-30',
    autocapture: false,
    capture_pageview: false,
    disable_session_recording: true,
    disable_surveys: true,
    // Suppresses the remote-config <script> injection that fires on every page
    // load independent of replay.
    disable_external_dependency_loading: true,
    person_profiles: 'identified_only',
    session_recording: {
      maskAllInputs: true,
      // maskAllInputs is the posthog-js DEFAULT and masks input VALUES only.
      // The sensitive material — story titles, tracked vendors, CVE lists — is
      // rendered text, which needs maskTextSelector.
      maskTextSelector: '*',
      blockSelector: '[data-ph-no-capture]',
    },
    // before_send, not sanitize_properties: the latter is deprecated upstream
    // and does not see every payload path.
    // Param is typed CaptureResult | null (posthog-js's own BeforeSendFn), not
    // the plan's structural `{ properties?: ... } | null`, which does not
    // satisfy BeforeSendFn — CaptureResult additionally requires `uuid` and
    // `event`. Behaviour is identical; only the annotation changed.
    before_send: (event: CaptureResult | null): CaptureResult | null =>
      decorateEvent(event, PATTERNS),
  })

  enabled = true
}

export function identifyUser(zitadelSub: string): void {
  if (!enabled || analyticsSuppressed || !zitadelSub) return
  posthog.identify(zitadelSub)
}

export function capture<K extends EventName>(name: K, props: EventProperties[K]): void {
  if (!enabled || analyticsSuppressed || !envelope) return
  // The envelope is NOT spread here — decorateEvent stamps it in before_send,
  // so it reaches $pageview, $identify and anything else posthog-js emits.
  posthog.capture(name, props)
}

export function setUserRole(role: UserRole): void {
  if (envelope) envelope.user_role = role
}
